import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { UploadIcon } from 'lucide-react';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { TagInput } from '@/components/tag-input';
import { showToast } from '@/components/ui/show-toast';
import { ProjectUploadModal } from '@/modules/verify/add-projects-modal';
import { useUploadFileMutation } from '@/services/files/queries';
import { useCreateProject, useUpdateProject } from '@/services/projects/queries';
import { useGetCountries } from '@/services/external/queries';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import type { Project } from '@/services/types';
import { useAppSearchParams } from '@/hooks/use-app-search-params';

type Props = {
  initialData?: Project;
  mode?: 'add' | 'edit';
  pageType?: 'verify' | 'projects';
  orgId?: string;
};

const projectFormSchema = z
  .object({
    title: z.string().min(1, 'Project title is required').max(100),
    summary: z.string().min(20, 'Summary should be at least 20 characters'),
    timeline: z.string().min(1, 'Timeline is required'),
    status: z.enum(['pending', 'completed', 'in-progress']),
    url: z.string().url('Invalid URL').optional().or(z.literal('')),
    file: z.instanceof(File).optional(),
    mediaName: z.string().optional(),
    continent: z.string().min(1, 'Continent is required'),
    country: z.string().min(1, 'Country is required'),
    region: z.string().min(1, 'Region is required'),
    state: z.string().min(1, 'State is required'),
    tags: z.array(z.string().min(1)).min(1, 'At least one tag is required'),
    collaboratingOrganisations: z
      .array(z.string().min(1)),
    keyOutcomes: z
      .string()
      .min(20, 'Key outcome should be at least 20 characters')
  })
  .refine(
    (data) => {
      const hasMedia = data.url || data.file;
      if (hasMedia && !data.mediaName?.trim()) {
        return false;
      }
      return true;
    },
    {
      message: 'Media name is required when providing a file or URL',
      path: ['mediaName']
    }
  );

export default function AddEditProjectForm({ initialData, mode = 'add', orgId, pageType = 'projects' }: Props) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaUrl, setMediaUrl] = useState('');
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const params = useAppSearchParams();

  const organisationId = orgId || user?.organisation?.id || '';
  const isEditMode = mode === 'edit';

  const { data: countries = [] } = useGetCountries();
  
  // Mutation hooks
  const { mutate: uploadFile, isPending: isUploading } = useUploadFileMutation(
    (response) => {
      // File upload successful, now create/update project with the URL
      const projectData: any = {
        title: form.getValues('title'),
        summary: form.getValues('summary'),
        timeline: form.getValues('timeline'),
        tags: form.getValues('tags'),
        collaboratingOrganisations: form.getValues('collaboratingOrganisations') || [],
        status: form.getValues('status') as 'pending' | 'completed',
        continent: form.getValues('continent'),
        country: form.getValues('country'),
        region: form.getValues('region'),
        state: form.getValues('state') || '',
        keyOutcomes: form.getValues('keyOutcomes')
      };
      
      // Only include media if we have both name and URL
      const mediaName = form.getValues('mediaName');
      if (mediaName?.trim() && response.url) {
        projectData.media = {
          name: mediaName,
          url: response.url
        };
      }
      
      if (isEditMode && initialData?.id) {
        updateProject({ id: initialData.id, data: projectData });
      } else {
        createProject({ ...projectData, organisationId });
      }
    },
    (error) => {
      showToast('error', error.response.data.message || 'Failed to upload media file. Please try again.');
      console.error('Upload error:', error);
    }
  );

  const { mutate: createProject, isPending: isCreatingProject } = useCreateProject(
    () => {
      showToast('success', 'Project created successfully');
      pageType === 'verify' ? params.set({view: 'experience'}) : navigate('/dashboard/projects');
      form.reset();
      setMediaFile(null);
      setMediaUrl('');
    },
    (error) => {
      showToast('error', error.response.data.message || 'Failed to create project. Please try again.');
      console.error('Project creation error:', error);
    }
  );

  const { mutate: updateProject, isPending: isUpdatingProject } = useUpdateProject(
    () => {
      showToast('success', 'Project updated successfully');
      form.reset();
      setMediaFile(null);
      setMediaUrl('');
    },
    (error) => {
      showToast('error', error.response.data.message || 'Failed to update project. Please try again.');
      console.error('Project update error:', error);
    }
  );

  const defaultEmpty = {
    title: '',
    summary: '',
    timeline: '',
    status: 'pending',
    url: '',
    file: undefined,
    mediaName: '',
    continent: '',
    country: '',
    region: '',
    state: '',
    tags: [],
    collaboratingOrganisations: [],
    keyOutcomes: ''
  }

  const form = useForm<z.infer<typeof projectFormSchema>>({
    resolver: zodResolver(projectFormSchema),
    defaultValues: useMemo(() => ({
      title: initialData?.title || defaultEmpty.title,
      summary: initialData?.summary || defaultEmpty.summary, 
      timeline: initialData?.timeline || defaultEmpty.timeline,
      status: (initialData?.status as "pending" | "completed" ) || defaultEmpty.status,
      url: initialData?.media?.url || defaultEmpty.url,
      mediaName: initialData?.media?.name || defaultEmpty.mediaName,
      continent: initialData?.continent || defaultEmpty.continent,
      country: initialData?.country || defaultEmpty.country,
      region: initialData?.region || defaultEmpty.region,
      state: initialData?.state || defaultEmpty.state,
      tags: initialData?.tags || defaultEmpty.tags,
      collaboratingOrganisations: initialData?.collaboratingOrganisations || defaultEmpty.collaboratingOrganisations,
      keyOutcomes: initialData?.keyOutcomes || defaultEmpty.keyOutcomes
    }), [initialData])
  });

  //set media file and url
  useEffect(() => {
    if (initialData?.media?.url) {
      setMediaUrl(initialData.media.url);
    }
  }, [initialData]);

  const statusOptions = [
    { label: 'Pending', value: 'pending' },
    { label: 'Completed', value: 'completed' },
  ];


  const uniqueContinents = Array.from(
    new Set(countries.map((c) => c.continent))
  )
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  const filteredSubregions = Array.from(
    new Set(
      countries
        .filter((country) => country.continent === form.watch('continent'))
        .map((c) => c.subregion)
    )
  )
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  const filteredCountriesBySubregion = countries
    .filter((country) => country.subregion === form.watch('region'))
    .sort((a, b) => a.name.localeCompare(b.name));

  const handleMediaSubmit = (
    file: File | null,
    url: string,
    mediaName: string
  ) => {
    if (file) {
      form.setValue('file', file);
      setMediaFile(file);
      form.setValue('url', '');
      setMediaUrl('');
    } else if (url) {
      form.setValue('url', url);
      setMediaUrl(url);
      form.setValue('file', undefined);
      setMediaFile(null);
    }
    form.setValue('mediaName', mediaName);
  };

  const onSubmit = async (formData: z.infer<typeof projectFormSchema>) => {
    if (!organisationId && !isEditMode) {
      showToast('error', 'Organisation data not available');
      return;
    }

    if (formData.file) {
      // Step 1: Upload file to get URL
      // Step 2: The upload success callback will automatically create/update project
      uploadFile({
        file: formData.file,
        name: formData.mediaName || ''
      });
    } else if (formData.url) {
      // For URL uploads, directly create/update project (no file upload needed)
      const projectData: any = {
        title: formData.title,
        summary: formData.summary,
        timeline: formData.timeline,
        tags: formData.tags,
        collaboratingOrganisations: formData.collaboratingOrganisations || [],
        status: formData.status as 'pending' | 'completed',
        continent: formData.continent,
        country: formData.country,
        region: formData.region,
        state: formData.state || '',
        keyOutcomes: formData.keyOutcomes
      };
      
      // Only include media if we have both name and URL
      if (formData.mediaName?.trim() && formData.url) {
        projectData.media = {
          name: formData.mediaName,
          url: formData.url
        };
      }
      
      if (isEditMode && initialData?.id) {
        updateProject({ id: initialData.id, data: projectData });
      } else {
        createProject({ ...projectData, organisationId });
      }
    } else {
      // No media provided - create/update project without media
      const projectData = {
        title: formData.title,
        summary: formData.summary,
        timeline: formData.timeline,
        tags: formData.tags,
        collaboratingOrganisations: formData.collaboratingOrganisations || [],
        status: formData.status as 'pending' | 'completed',
        continent: formData.continent,
        country: formData.country,
        region: formData.region,
        state: formData.state || '',
        keyOutcomes: formData.keyOutcomes
      };
      
      if (isEditMode && initialData?.id) {
        updateProject({ id: initialData.id, data: projectData });
      } else {
        createProject({ ...projectData, organisationId });
      }
    }
  };

  return (
    <>
      <div>
        <h1 className='text-2xl font-bold'>{isEditMode ? 'Edit Project' : 'Add New Project'}</h1>
        <p className='mt-2 text-gray-600'>
          {isEditMode ? 'Update your project details' : 'Tell us about your project'}
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-5'>
          <FormField
            control={form.control}
            name='title'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='text-base font-medium text-[#111014]'>
                  Project Title
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder='Your project name'
                    {...field}
                    className='bg-gray-50'
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='summary'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='text-base font-medium text-[#111014]'>
                  Project Summary
                </FormLabel>
                <FormDescription>
                  Tell us a little about your project
                </FormDescription>
                <FormControl>
                  <Textarea
                    placeholder='Write a summary here'
                    className='h-32 resize-none bg-gray-50'
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='timeline'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='text-base font-medium text-[#111014]'>
                  Timeline
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder='Enter timeline (e.g., Jan 2022 - Dec 2023)'
                    {...field}
                    className='bg-gray-50'
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='status'
            render={({ field }) => (
              <FormItem className='space-y-2'>
                <FormLabel className='text-base font-medium text-[#111014]'>
                  Project Status
                </FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger className='h-12 bg-gray-50'>
                      <SelectValue placeholder='Select status' />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {statusOptions.map((stat) => (
                      <SelectItem key={stat.value} value={stat.value}>
                        {stat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage className='text-sm text-red-500' />
              </FormItem>
            )}
          />

          <div className='grid grid-cols-2 gap-4'>
            <FormField
              control={form.control}
              name='continent'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='text-base font-medium text-[#111014]'>
                    Continent
                  </FormLabel>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value);
                      form.setValue('country', '');
                      form.setValue('region', '');
                    }}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger className='h-12 bg-gray-50'>
                        <SelectValue placeholder='Select continent' />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {uniqueContinents.map((continent) => (
                        <SelectItem key={continent} value={continent}>
                          {continent}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='region'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='text-base font-medium text-[#111014]'>
                    Region
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    // disabled={!form.watch('country')}
                  >
                    <FormControl>
                      <SelectTrigger className='h-12 bg-gray-50'>
                        <SelectValue
                          placeholder={
                            form.watch('continent')
                              ? 'Select region'
                              : 'Select continent first'
                          }
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {filteredSubregions.map((subregion, index) => (
                        <SelectItem key={index} value={subregion}>
                          {subregion}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='country'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='text-base font-medium text-[#111014]'>
                    Country
                  </FormLabel>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value);
                      // form.setValue('region', '');
                    }}
                    value={field.value}
                    disabled={!form.watch('region')}
                  >
                    <FormControl>
                      <SelectTrigger className='h-12 bg-gray-50'>
                        <SelectValue
                          placeholder={
                            form.watch('region')
                              ? 'Select country'
                              : 'Select region first'
                          }
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className='max-h-[300px] overflow-y-auto'>
                      {filteredCountriesBySubregion.map((country) => (
                        <SelectItem key={country.name} value={country.name}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='state'
              render={({ field }) => (
                <FormItem>
                  <FormLabel className='text-base font-medium text-[#111014]'>
                    State
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder='Enter State'
                      {...field}
                      className='bg-gray-50'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name='collaboratingOrganisations'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='text-base font-medium text-[#111014]'>
                  Collaborating Organisations
                </FormLabel>
                <FormControl>
                  <TagInput
                    {...field}
                    placeholder='Add an organization and press space bar'
                    tags={field.value}
                    className='bg-gray-50'
                    setTags={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='keyOutcomes'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='text-base font-medium text-[#111014]'>
                  Key Outcome
                </FormLabel>
                <FormDescription>
                  Describe the main achievements or results of this project
                </FormDescription>
                <FormControl>
                  <Textarea
                    placeholder='Describe the key outcomes of your project'
                    className='h-32 resize-none bg-gray-50'
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className='flex flex-col space-y-2'>
            <FormLabel className='text-base font-medium text-[#111014]'>
              Project Media <span className='text-sm font-normal text-gray-500'>(Optional)</span>
            </FormLabel>
            {mediaFile && (
              <div className='flex items-center gap-2'>
                <span className='text-sm'>{mediaFile.name}</span>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => {
                    setMediaFile(null);
                    form.setValue('file', undefined);
                  }}
                >
                  Remove
                </Button>
              </div>
            )}
            {mediaUrl && (
              <div className='flex items-center gap-2'>
                <span className='text-sm'>{mediaUrl}</span>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => {
                    setMediaUrl('');
                    form.setValue('url', '');
                  }}
                >
                  Remove
                </Button>
              </div>
            )}
            <Button type='button' onClick={() => setIsModalOpen(true)}>
              <UploadIcon className='mr-2 h-4 w-4' />
              {mediaFile || mediaUrl ? 'Change Media' : 'Add Media'}
            </Button>
          </div>

          <FormField
            control={form.control}
            name='tags'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='text-base font-medium text-[#111014]'>
                  Add Tags *
                </FormLabel>
                <FormControl>
                  <TagInput
                    {...field}
                    placeholder='Add a tag and press space bar'
                    tags={field.value}
                    className='bg-gray-50'
                    setTags={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          { pageType === 'verify' && <div className='flex justify-between pt-4'>
            <Button
              type='button'
              variant='outline'
              onClick={() => params.set({view: 'media'})}
            >
              Cancel
            </Button>
            <Button 
              type='submit' 
              disabled={isUploading || isCreatingProject || isUpdatingProject} 
              isLoading={isUploading || isCreatingProject || isUpdatingProject}
            >
              {isEditMode ? 'Update Project' : 'Save Project'}
            </Button>
          </div> }

          { pageType === 'projects' && <div className='flex justify-between pt-4'>
            <Button
              type='button'
              variant='outline'
              onClick={() => navigate('/dashboard/projects')}
            >
              Cancel
            </Button>
            <Button 
              type='submit' 
              disabled={isUploading || isCreatingProject || isUpdatingProject} 
              isLoading={isUploading || isCreatingProject || isUpdatingProject}
            >
              {isEditMode ? 'Update Project' : 'Create Project'}
            </Button>
          </div> }
        </form>
      </Form>

      <ProjectUploadModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        onSubmit={handleMediaSubmit}
        initialFile={mediaFile}
        initialUrl={mediaUrl}
        initialMediaName={''}
      />
    </> 
  );
}